/**
 * Login Page
 *
 * Displays email/password authentication and Google Sign-In button.
 * Supports both sign-in and sign-up flows.
 * Redirects to home page (or original destination) after successful login.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

type AuthMode = 'signin' | 'signup';

/**
 * Google logo SVG component
 */
const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.166 6.656 3.58 9 3.58z"
    />
  </svg>
);

/**
 * Login page component with email/password and Google Sign-In
 */
export const LoginPage = () => {
  const { user, loading, error, signIn, signInWithEmail, signUpWithEmail, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Form state
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Get the intended destination after login (default to /climb)
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/climb';

  // Redirect if already authenticated
  useEffect(() => {
    if (user && !loading) {
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, from]);

  // Clear form error when switching modes
  useEffect(() => {
    setFormError(null);
    clearError();
  }, [mode, clearError]);

  // Reset form fields when component mounts (e.g., after sign out)
  useEffect(() => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setConfirmPassword('');
    setFormError(null);
  }, []);

  /**
   * Validate email format
   */
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    clearError();

    // Validate email
    if (!isValidEmail(email)) {
      setFormError('Please enter a valid email address');
      return;
    }

    // Validate password
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }

    if (mode === 'signup') {
      // Validate display name
      if (!displayName.trim()) {
        setFormError('Please enter your name');
        return;
      }

      // Validate password confirmation
      if (password !== confirmPassword) {
        setFormError('Passwords do not match');
        return;
      }

      try {
        await signUpWithEmail(email, password, displayName.trim());
      } catch {
        // Error is handled by AuthContext
      }
    } else {
      try {
        await signInWithEmail(email, password);
      } catch {
        // Error is handled by AuthContext
      }
    }
  };

  /**
   * Handle Google sign in button click
   */
  const handleGoogleSignIn = async () => {
    try {
      clearError();
      setFormError(null);
      await signIn();
    } catch {
      // Error is handled by AuthContext
    }
  };

  /**
   * Toggle between sign-in and sign-up modes
   */
  const toggleMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    setPassword('');
    setConfirmPassword('');
  };

  // Don't show login page if already authenticated
  if (user && !loading) {
    return null;
  }

  return (
    <div
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backgroundColor: 'var(--color-bg-secondary)',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: 'var(--shadow-lg)',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
        }}
      >
        {/* Logo / Title */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '8px',
          }}
        >
          <img
            src="/icons/swag-s-ligntning-bolt.png"
            alt="Flash logo"
            style={{ width: '48px', height: '48px' }}
          />
          <h1
            style={{
              fontSize: '2rem',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              margin: 0,
            }}
          >
            Flash
          </h1>
        </div>
        <p
          style={{
            color: 'var(--color-text-secondary)',
            marginBottom: '32px',
            fontSize: '0.875rem',
          }}
        >
          Track your climbing progression
        </p>

        {/* Error Message */}
        {(error || formError) && (
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '24px',
              color: '#991b1b',
              fontSize: '0.875rem',
            }}
          >
            {formError ?? error}
          </div>
        )}

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit}>
          {mode === 'signup' && (
            <input
              type="text"
              placeholder="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 16px',
                marginBottom: '12px',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                fontSize: '1rem',
                boxSizing: 'border-box',
                backgroundColor: 'var(--color-bg-primary)',
                color: 'var(--color-text-primary)',
              }}
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 16px',
              marginBottom: '12px',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              fontSize: '1rem',
              boxSizing: 'border-box',
              backgroundColor: 'var(--color-bg-primary)',
              color: 'var(--color-text-primary)',
            }}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 16px',
              marginBottom: '12px',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              fontSize: '1rem',
              boxSizing: 'border-box',
              backgroundColor: 'var(--color-bg-primary)',
              color: 'var(--color-text-primary)',
            }}
          />
          {mode === 'signup' && (
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 16px',
                marginBottom: '12px',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                fontSize: '1rem',
                boxSizing: 'border-box',
                backgroundColor: 'var(--color-bg-primary)',
                color: 'var(--color-text-primary)',
              }}
            />
          )}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 24px',
              backgroundColor: 'var(--color-primary)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 500,
              color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              marginBottom: '16px',
            }}
          >
            {loading ? 'Please wait...' : mode === 'signin' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        {/* Mode Toggle */}
        <p
          style={{
            fontSize: '0.875rem',
            color: 'var(--color-text-secondary)',
            marginBottom: '24px',
          }}
        >
          {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
          <button
            onClick={toggleMode}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-primary)',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              padding: 0,
            }}
          >
            {mode === 'signin' ? 'Sign up' : 'Sign in'}
          </button>
        </p>

        {/* Divider */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--color-border)' }} />
          <span
            style={{ padding: '0 16px', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}
          >
            or continue with
          </span>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--color-border)' }} />
        </div>

        {/* Google Sign-In Button */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            width: '100%',
            padding: '12px 24px',
            backgroundColor: 'var(--color-bg-primary)',
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 500,
            color: 'var(--color-text-primary)',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'all 0.2s',
          }}
        >
          <GoogleLogo />
          <span>Google</span>
        </button>

        {/* Terms */}
        <p
          style={{
            marginTop: '24px',
            fontSize: '0.75rem',
            color: 'var(--color-text-muted)',
          }}
        >
          By signing in, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
};
